import { createServerStateStore } from "./serverState";

export type IncomeExpenseType = "Gelir" | "Gider";
export type IncomeExpenseMode = "Sabit" | "Tek Sefer";
export type IncomeExpensePaymentMethod = "Nakit" | "Havale";

export interface IncomeExpenseEntry {
  id: number;
  tur: IncomeExpenseType;
  tekrarTipi: IncomeExpenseMode;
  odemeTipi: IncomeExpensePaymentMethod;
  tutar: number;
  kalemAdi: string;
  tarih: string;
  aciklama?: string;
  linkedTransferIds?: string[];
  linkedTransferFisNos?: string[];
  createdAt?: string;
  updatedAt?: string;
}

function normalizeType(value?: string): IncomeExpenseType {
  return value === "Gider" ? "Gider" : "Gelir";
}

function normalizeMode(value?: string): IncomeExpenseMode {
  return value === "Sabit" ? "Sabit" : "Tek Sefer";
}

function normalizePaymentMethod(value?: string): IncomeExpensePaymentMethod {
  return value === "Havale" ? "Havale" : "Nakit";
}

function normalizeStringList(value: unknown): string[] {
  if (!Array.isArray(value)) return [];
  return value.map((item) => String(item ?? "").trim()).filter(Boolean);
}

function normalizeRecord(item: any): IncomeExpenseEntry {
  return {
    id: Number(item?.id ?? 0),
    tur: normalizeType(item?.tur),
    tekrarTipi: normalizeMode(item?.tekrarTipi),
    odemeTipi: normalizePaymentMethod(item?.odemeTipi),
    tutar: Number(item?.tutar ?? 0),
    kalemAdi: String(item?.kalemAdi ?? "").trim(),
    tarih: String(item?.tarih ?? ""),
    aciklama:
      item?.aciklama === undefined ? undefined : String(item.aciklama ?? ""),
    linkedTransferIds: normalizeStringList(item?.linkedTransferIds),
    linkedTransferFisNos: normalizeStringList(item?.linkedTransferFisNos),
    createdAt:
      item?.createdAt === undefined ? undefined : String(item.createdAt ?? ""),
    updatedAt:
      item?.updatedAt === undefined ? undefined : String(item.updatedAt ?? ""),
  };
}

function normalizeRecords(value: unknown): IncomeExpenseEntry[] {
  if (!Array.isArray(value)) return [];

  return value
    .map((item) => normalizeRecord(item))
    .filter(
      (item) =>
        Number.isFinite(item.id) &&
        item.id > 0 &&
        item.kalemAdi.length > 0 &&
        item.tarih.length > 0
    );
}

const store = createServerStateStore<IncomeExpenseEntry[]>(
  "income_expense_entries",
  [],
  normalizeRecords
);

export function getIncomeExpenseEntries() {
  return store.getState();
}

export async function rehydrateIncomeExpenseEntries() {
  return store.rehydrate();
}

export function subscribeIncomeExpenseEntries(listener: () => void) {
  return store.subscribe(listener);
}

export async function setAllIncomeExpenseEntries(
  updater:
    | IncomeExpenseEntry[]
    | ((prev: IncomeExpenseEntry[]) => IncomeExpenseEntry[])
) {
  return store.setState((prev) =>
    normalizeRecords(typeof updater === "function" ? updater(prev) : updater)
  );
}

export async function upsertIncomeExpenseEntry(
  item: Omit<IncomeExpenseEntry, "id" | "createdAt" | "updatedAt"> & {
    id?: number;
  }
) {
  return setAllIncomeExpenseEntries((prev) => {
    const now = new Date().toISOString();

    if (item.id) {
      const index = prev.findIndex((row) => row.id === item.id);
      if (index >= 0) {
        const next = [...prev];
        next[index] = normalizeRecord({
          ...next[index],
          ...item,
          updatedAt: now,
        });
        return next;
      }
    }

    const nextId =
      prev.length > 0 ? Math.max(...prev.map((row) => row.id)) + 1 : 1;

    return [
      normalizeRecord({
        ...item,
        id: nextId,
        createdAt: now,
        updatedAt: now,
      }),
      ...prev,
    ];
  });
}

export async function removeIncomeExpenseEntry(id: number) {
  return setAllIncomeExpenseEntries((prev) =>
    prev.filter((row) => row.id !== id)
  );
}
